import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Container, Row } from 'reactstrap';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import BreadCrumb2 from 'Components/pos2u/BreadCrumb2';
import SuccessModal from 'Components/pos2u/Modals/SuccessModal';
import ModalBackdropNotFound from 'Components/pos2u/ModalBackdropNotFound';
import { PROMOTIONS } from 'Routes/constants';
import AlertWrapper from 'pages/Promotions/PromotionManagement/AlertWrapper';
import FormActionButtons from 'pages/Promotions/PromotionManagement/FormActionButtons';
import { initialValues } from 'pages/Promotions/PromotionManagement/InitialValues';
import PromoActivation from 'pages/Promotions/PromotionManagement/PromoActivation';
import PromoInfo from 'pages/Promotions/PromotionManagement/PromoInfo';
import PromoPeriod from 'pages/Promotions/PromotionManagement/PromoPeriod';
import PromoProducts from 'pages/Promotions/PromotionManagement/PromoProducts';
import { validationSchema } from 'pages/Promotions/PromotionManagement/ValidationSchema';
import PromoConditionAndDiscount from 'pages/Promotions/PromotionManagement/promotionCondition/PromoConditionAndDiscount';
import {
  getPromotionByID,
  postAddPromotion,
  putUpdatePromotion,
} from 'services/promotionService';
import { getStoresInfo } from 'services/storeService';
import { RootState } from 'slices';
import {
  apiError,
  apiSuccess,
  resetPromotion,
  setBranchOptions,
  setIsEdit,
  setModalBackdrop,
  setselectMultiBranches,
  setselectMultiCategories,
  setselectMultiFreePromoCategories,
  setselectMultiFreePromoProducts,
  setselectMultiProducts,
} from 'slices/promotions/reducer';
import 'styles/common/checked-input-style.css';
import { PromoFormValues } from 'types/promotion/promotionTypes';
import { convertPromoConditionToSelectedData } from 'utils/common/promotions/converter';
import { mapPromoAPIToPromoForm } from 'utils/mappers/promoMapper';
import { convertToStoresInfoToBranchOptions2 } from 'utils/stores/converter';

const SELECT_FIELD_KEYS = {
  branch: 2,
  promoType: 3,
} as const;

const OBJECT_ERROR_FIELDS = ['promoCondition', 'promoDiscount', 'promoPeriod'];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const focusElement = (selector: string) => {
  const element = document.querySelector(selector) as HTMLElement | null;
  element?.focus();
};

const getPromoItemImageUrl = (
  image?: { thumbs?: string; large?: string } | null
) => image?.thumbs?.trim() || image?.large?.trim() || undefined;

const getFirstNestedErrorPath = (
  basePath: string,
  value: unknown
): string | null => {
  if (Array.isArray(value)) {
    const firstIndex = value.findIndex((item) => item !== undefined && item !== null);

    if (firstIndex === -1) {
      return null;
    }

    const nestedPath = getFirstNestedErrorPath(
      `${basePath}[${firstIndex}]`,
      value[firstIndex]
    );

    return nestedPath ?? `${basePath}[${firstIndex}]`;
  }

  if (isPlainObject(value)) {
    const [firstKey] = Object.keys(value);

    if (!firstKey) {
      return null;
    }

    const nestedPath = getFirstNestedErrorPath(
      `${basePath}.${firstKey}`,
      value[firstKey]
    );

    return nestedPath ?? `${basePath}.${firstKey}`;
  }

  return basePath;
};

const PromotionManagementPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const dispatch: ThunkDispatch<RootState, void, Action> = useDispatch();

  const promotionsState = useSelector((state: RootState) => state.Promotions);
  const { isEdit, countSubmit, error, BranchOptions, modalBackdrop } =
    promotionsState;

  const alertRef = useRef<HTMLDivElement | null>(null);
  const formikRef = useRef<FormikProps<PromoFormValues>>(null);

  const [editData, setEditData] = useState<PromoFormValues | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);

  useEffect(() => {
    document.title = `${id ? t('Edit_Promotion') : t('Add_Promotion')} | POS2U`;
  }, [id, t]);

  const fetchStoresInfo = useCallback(async () => {
    try {
      const response = await getStoresInfo();

      if (!response) {
        return;
      }

      const branchOptions = await convertToStoresInfoToBranchOptions2(
        response.stores
      );
      dispatch(setBranchOptions(branchOptions));
    } catch (fetchError) {
      console.error('Failed to fetch stores info:', fetchError);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchStoresInfo();
  }, [fetchStoresInfo]);

  const syncEditSelections = useCallback(
    async (values: PromoFormValues, promo: Awaited<ReturnType<typeof getPromotionByID>>) => {
      const { branch, promoProducts, ConditionType, promoFreeGift } = values;

      if (BranchOptions.length > 0) {
        const selectedBranches = BranchOptions.filter((branchOption) =>
          branch.includes(parseInt(branchOption.value, 10))
        );
        dispatch(setselectMultiBranches(selectedBranches));
      }

      if (ConditionType === '2' && promoProducts.info.length > 0) {
        const selectedCategories = await convertPromoConditionToSelectedData(
          promoProducts.info
        );
        dispatch(setselectMultiCategories(selectedCategories));
      }

      if (ConditionType === '1' && promoProducts.info.length > 0) {
        const selectedProducts = await convertPromoConditionToSelectedData(
          promoProducts.info,
          1
        );
        dispatch(setselectMultiProducts(selectedProducts));
      }

      if (
        promo &&
        promoFreeGift.freeList.length > 0 &&
        promo.promotion_free_items?.free_list?.length
      ) {
        const selectedFreeItems = promo.promotion_free_items.free_list.map(
          (item) => ({
            label: item.name || item.code.toString(),
            value: item.code.toString(),
            image: getPromoItemImageUrl(item.image),
          })
        );

        if (promoFreeGift.freeType === '2') {
          dispatch(setselectMultiFreePromoCategories(selectedFreeItems));
        }

        if (promoFreeGift.freeType === '1') {
          dispatch(setselectMultiFreePromoProducts(selectedFreeItems));
        }
      }
    },
    [BranchOptions, dispatch]
  );

  useEffect(() => {
    if (!id || BranchOptions.length === 0) {
      return;
    }

    let isCancelled = false;

    const loadPromoData = async () => {
      dispatch(setIsEdit(true));

      try {
        const promo = await getPromotionByID(id);

        if (isCancelled) {
          return;
        }

        if (!promo) {
          setIsNotFound(true);
          return;
        }

        const transformedEditData = await mapPromoAPIToPromoForm(promo);

        if (isCancelled) {
          return;
        }

        setEditData(transformedEditData);
        await syncEditSelections(transformedEditData, promo);
      } catch (loadError) {
        if (!isCancelled) {
          console.error('Failed to load promo data:', loadError);
        }
      }
    };

    loadPromoData();

    return () => {
      isCancelled = true;
    };
  }, [BranchOptions, dispatch, id, syncEditSelections]);

  useEffect(() => {
    const formik = formikRef.current;

    if (!formik?.errors) {
      return;
    }

    const errorKeys = Object.keys(formik.errors);
    const [firstErrorKey] = errorKeys;

    if (!firstErrorKey) {
      return;
    }

    const firstErrorField = document.querySelector(
      `[name="${firstErrorKey}"]`
    ) as HTMLInputElement | null;

    if (
      firstErrorField &&
      firstErrorField.type === 'hidden' &&
      firstErrorField.name in SELECT_FIELD_KEYS
    ) {
      const selectKey =
        SELECT_FIELD_KEYS[firstErrorField.name as keyof typeof SELECT_FIELD_KEYS];
      focusElement(`#react-select-${selectKey}-input`);
      return;
    }

    const firstErrorValue =
      formik.errors[firstErrorKey as keyof typeof formik.errors];

    if (firstErrorKey === 'promoPeriod' && isPlainObject(firstErrorValue)) {
      if ('days' in firstErrorValue) {
        focusElement('#toggleAllDays');
        return;
      }

      const nestedPath = getFirstNestedErrorPath(firstErrorKey, firstErrorValue);

      if (nestedPath) {
        focusElement(`[name="${nestedPath}"]`);
      }
      return;
    }

    if (OBJECT_ERROR_FIELDS.includes(firstErrorKey)) {
      const nestedPath = getFirstNestedErrorPath(firstErrorKey, firstErrorValue);

      if (nestedPath) {
        focusElement(`[name="${nestedPath}"]`);
      }
      return;
    }

    firstErrorField?.focus();
  }, [countSubmit]);

  useEffect(() => {
    formikRef.current?.validateForm();
  }, [i18n.language]);

  useEffect(() => {
    if (error !== '') {
      alertRef.current?.focus();
    }
  }, [error]);

  useEffect(() => {
    return () => {
      dispatch(setModalBackdrop(false));
    };
  }, [dispatch]);

  const handleCloseSuccessModal = useCallback(() => {
    formikRef.current?.resetForm();
    setEditData(null);
    setIsNotFound(false);
    dispatch(resetPromotion());
    dispatch(setModalBackdrop(false));
  }, [dispatch]);

  const toggleModalBackDrop = useCallback(() => {
    dispatch(setModalBackdrop(!modalBackdrop));
  }, [dispatch, modalBackdrop]);

  const submitPromotionForm = useCallback(
    async (values: PromoFormValues) => {
      try {
        const response =
          isEdit && id
            ? await putUpdatePromotion(values, id)
            : await postAddPromotion(values);

        const { message, error_code } = response.data;

        if (message === 'OK' && error_code === 0) {
          dispatch(apiSuccess({ message }));
          return;
        }

        dispatch(apiError({ message, error_code }));
      } catch (submitError) {
        const errorMessage =
          submitError instanceof Error
            ? submitError.message
            : String(submitError);

        dispatch(apiError({ message: errorMessage }));
        console.error('Error updating product details:', submitError);
      }
    },
    [dispatch, id, isEdit]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {error !== '' && (
            <div
              ref={alertRef}
              tabIndex={-1}
              aria-live="assertive"
              className="mb-3"
            >
              <AlertWrapper
                type="error"
                message={error}
                heading={t('something_went_wrong')}
              />
            </div>
          )}

          <ModalBackdropNotFound
            isNotFound={isNotFound}
            setIsNotFound={setIsNotFound}
            message={`${t('Not found for Promotion ID')} ${id}`}
            redirect={PROMOTIONS.LIST}
            ButtonMsg={t('Btn_GoBackToPromotions')}
            icon="bx bx-arrow-back"
          />

          <SuccessModal
            isOpen={modalBackdrop}
            onClose={toggleModalBackDrop}
            goBackTo={PROMOTIONS.LIST}
            addAnotherTo={PROMOTIONS.ADD}
            goBackLabel={t('Btn_GoBackToPromotions')}
            addAnotherLabel={t('Btn_Add_Another_Promotion')}
            onGoBack={handleCloseSuccessModal}
            onAddAnother={handleCloseSuccessModal}
          />

          <BreadCrumb2
            title={id ? t('Edit_Promotion') : t('Add_Promotion')}
            pageTitle={t('Promotion')}
            linkId="promo-link"
            link={PROMOTIONS.LIST}
          />

          <Formik<PromoFormValues>
            initialValues={isEdit ? initialValues(editData) : initialValues(null)}
            validationSchema={validationSchema(t)}
            onSubmit={submitPromotionForm}
            enableReinitialize={true}
            innerRef={formikRef}
          >
            {(formik) => (
              <form noValidate onSubmit={formik.handleSubmit}>
                <Row>
                  <PromoInfo formik={formik} dispatch={dispatch} />
                </Row>
                <Row>
                  <PromoPeriod formik={formik} />
                </Row>
                <Row>
                  <PromoProducts formik={formik} />
                </Row>
                <Row>
                  <PromoConditionAndDiscount formik={formik} />
                </Row>
                <Row>
                  <PromoActivation formik={formik} />
                </Row>
                <FormActionButtons dispatch={dispatch} formik={formik} />
              </form>
            )}
          </Formik>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PromotionManagementPage;
